/**
 * AcceptInvitation Page
 *
 * Page for accepting household invitations via link or code.
 * Route: /invite/:token
 *
 * Handles:
 * - Preview of invitation details (works without authentication)
 * - Redirect to login if not authenticated
 * - Accept/decline invitation if authenticated
 * - WP-HOUSEHOLD-2: Auto-switch to invited household and show welcome modal
 */

import { useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Card, Button, Spinner, Alert } from '@/components/ui';
import { WelcomeHouseholdModal } from '@/components/household';
import { useInvitationPreview, useAcceptInvitation } from '@/hooks/useHouseholds';
import { useAuthStore } from '@/store/authStore';
import { setAccessToken } from '@/services/api';
import { toast } from '@/components/ui/Toaster';

/**
 * Format expiration time for display
 */
function formatExpiry(expiresAt: string): string {
  const date = new Date(expiresAt);
  const now = new Date();
  const diffMs = date.getTime() - now.getTime();

  if (diffMs <= 0) {
    return 'expired';
  }

  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  if (diffHours < 1) {
    const diffMins = Math.floor(diffMs / (1000 * 60));
    return `${diffMins} minute${diffMins !== 1 ? 's' : ''}`;
  } else if (diffHours < 24) {
    return `${diffHours} hour${diffHours !== 1 ? 's' : ''}`;
  } else {
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays} day${diffDays !== 1 ? 's' : ''}`;
  }
}

export default function AcceptInvitation() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isAuthLoading = useAuthStore((state) => state.isLoading);
  const user = useAuthStore((state) => state.user);
  const setUser = useAuthStore((state) => state.setUser);

  const [hasAttemptedAccept, setHasAttemptedAccept] = useState(false);
  // WP-BUG-35: Track successful acceptance to prevent race condition with cache clear
  const [hasAccepted, setHasAccepted] = useState(false);

  // WP-HOUSEHOLD-2: State for welcome modal
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [joinedHouseholdName, setJoinedHouseholdName] = useState('');
  const [joinedHouseholdId, setJoinedHouseholdId] = useState('');

  // Fetch invitation preview
  const {
    data: preview,
    isLoading: previewLoading,
    error: previewError,
  } = useInvitationPreview(token || '');

  const acceptInvitation = useAcceptInvitation();

  // Handle accept invitation
  // WP-HOUSEHOLD-2: Auto-switch to invited household and show welcome modal
  const handleAccept = useCallback(async () => {
    if (!token) return;

    setHasAttemptedAccept(true);

    try {
      const result = await acceptInvitation.mutateAsync(token);

      // WP-BUG-35: CRITICAL - Mark as accepted BEFORE any cache operations
      // This prevents the race condition where cache clear triggers refetch
      // that returns isValid: false and shows "Invitation Expired"
      setHasAccepted(true);

      // WP-AUTH-12: Only update access token - refresh token is managed via httpOnly cookie
      setAccessToken(result.accessToken);

      // Update user context with new tenant info
      if (user) {
        setUser({
          ...user,
          tenantId: result.tenantId,
          role: result.role,
        });
      }

      // Clear all cached data - it's tenant-scoped
      // This forces refetch with new tenant context
      queryClient.clear();

      // Check if this is the user's first additional household
      if (result.isFirstAdditionalHousehold) {
        // Show welcome modal instead of immediate navigation
        setJoinedHouseholdName(result.tenantName);
        setJoinedHouseholdId(result.tenantId);
        setShowWelcomeModal(true);
      } else {
        // 3rd+ household - just show toast and navigate
        toast.success(`Welcome! You have joined ${result.tenantName}`);
        navigate('/dashboard');
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to accept invitation';
      toast.error(message);
    }
  }, [token, acceptInvitation, navigate, user, setUser, queryClient]);

  // Handle decline
  const handleDecline = useCallback(() => {
    navigate('/dashboard');
  }, [navigate]);

  // Handle welcome modal close
  // WP-HOUSEHOLD-2: Must be defined before early returns per React hooks rules
  const handleWelcomeModalClose = useCallback(() => {
    setShowWelcomeModal(false);
  }, []);

  // Show loading state while checking auth
  if (isAuthLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Spinner size="lg" />
      </div>
    );
  }

  // Show loading state while fetching preview
  if (previewLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Spinner size="lg" />
          <p className="mt-4 text-gray-600">Loading invitation...</p>
        </div>
      </div>
    );
  }

  // Show error if invitation not found
  if (previewError || !preview) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="max-w-md w-full">
          <div className="text-center py-8">
            <div className="mx-auto w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mb-4">
              <svg
                className="w-6 h-6 text-red-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>
            <h1 className="text-xl font-semibold text-gray-900 mb-2">Invitation Not Found</h1>
            <p className="text-gray-600 mb-6">
              This invitation may have expired, been revoked, or does not exist.
            </p>
            <Link to="/dashboard">
              <Button variant="primary">Go to Dashboard</Button>
            </Link>
          </div>
        </Card>
      </div>
    );
  }

  // Show error if invitation is invalid/expired
  // WP-BUG-35: Don't show expired screen if we just successfully accepted
  // (cache clear triggers refetch that returns isValid: false)
  if (!preview.isValid && !hasAccepted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="max-w-md w-full">
          <div className="text-center py-8">
            <div className="mx-auto w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center mb-4">
              <svg
                className="w-6 h-6 text-yellow-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h1 className="text-xl font-semibold text-gray-900 mb-2">Invitation Expired</h1>
            <p className="text-gray-600 mb-6">
              This invitation to join <strong>{preview.householdName}</strong> has expired.
              Please ask for a new invitation.
            </p>
            <Link to="/dashboard">
              <Button variant="primary">Go to Dashboard</Button>
            </Link>
          </div>
        </Card>
      </div>
    );
  }

  // Show login prompt if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="max-w-md w-full">
          <div className="text-center py-8">
            <div className="mx-auto w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center mb-4">
              <svg
                className="w-6 h-6 text-primary-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
            </div>
            <h1 className="text-xl font-semibold text-gray-900 mb-2">
              You&apos;re Invited to Join
            </h1>
            <p className="text-2xl font-bold text-primary-600 mb-2">{preview.householdName}</p>
            {preview.inviterName && (
              <p className="text-gray-600 mb-2">Invited by {preview.inviterName}</p>
            )}
            <p className="text-sm text-gray-500 mb-6">
              Expires in {formatExpiry(preview.expiresAt)}
            </p>

            <div className="space-y-3">
              <p className="text-sm text-gray-600">
                Please sign in or create an account to accept this invitation.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link to={`/login?redirect=/invite/${token}`}>
                  <Button variant="primary" className="w-full sm:w-auto">
                    Sign In
                  </Button>
                </Link>
                <Link to={`/register?redirect=/invite/${token}`}>
                  <Button variant="outline" className="w-full sm:w-auto">
                    Create Account
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  // Show invitation details with accept/decline buttons (authenticated)
  return (
    <>
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="max-w-md w-full">
          <div className="text-center py-8">
            <div className="mx-auto w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center mb-4">
              <svg
                className="w-6 h-6 text-primary-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
            </div>
            <h1 className="text-xl font-semibold text-gray-900 mb-2">
              You&apos;re Invited to Join
            </h1>
            <p className="text-2xl font-bold text-primary-600 mb-2">{preview.householdName}</p>
            {preview.inviterName && (
              <p className="text-gray-600 mb-2">Invited by {preview.inviterName}</p>
            )}
            <p className="text-sm text-gray-500 mb-6">
              Expires in {formatExpiry(preview.expiresAt)}
            </p>

            {acceptInvitation.isError && hasAttemptedAccept && (
              <Alert variant="error" className="mb-4">
                {acceptInvitation.error?.message || 'Failed to accept invitation'}
              </Alert>
            )}

            <Alert variant="info" className="mb-4">
              Joining this household won't affect your personal household.
              You'll be able to switch between them anytime using the
              household switcher in the navigation bar.
            </Alert>

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                variant="primary"
                onClick={handleAccept}
                isLoading={acceptInvitation.isPending}
                className="w-full sm:w-auto"
              >
                Accept Invitation
              </Button>
              <Button
                variant="outline"
                onClick={handleDecline}
                disabled={acceptInvitation.isPending}
                className="w-full sm:w-auto"
              >
                Decline
              </Button>
            </div>
          </div>
        </Card>
      </div>

      {/* WP-HOUSEHOLD-2: Welcome modal for first additional household */}
      <WelcomeHouseholdModal
        isOpen={showWelcomeModal}
        onClose={handleWelcomeModalClose}
        joinedHouseholdName={joinedHouseholdName}
        joinedHouseholdId={joinedHouseholdId}
      />
    </>
  );
}
